import { AdminMessage } from "@components/admin/admin-helpers"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { GallerySlider } from "@components/media/GallerySlider"
import { ParallaxCarousel } from "@components/media/ParallaxCarousel"
import { PlayTrailerButton } from "@components/media/TrailerOverlay"
import { Gradient, GradientType } from "@components/motion/Gradient"
import { Button } from "@components/ui/Button"
import { Checkbox } from "@components/ui/Checkbox"
import { IconChevronLeft } from "@components/ui/Icon"
import { useIsMobile } from "@hooks/useIsMobile"
import dayjs from "dayjs"
import { defineBlock, EditableText, InspectorControls, useInlineEditableValue, usePostTitleEditor } from "eddev/blocks"
import { Link } from "eddev/routing"
import { ReactNode, useRef } from "react"

export const meta: BlockMeta = {
  title: "Header",
  icon: "flag",
  tags: ["film-root"],
  inserter: false,
  conditions: {
    postTypes: ["film"],
  },
}

export default defineBlock("film/film-header", (props) => {
  const gradientRef = useRef<GradientType>(null)
  const [itemConfig, setItemConfig] = useInlineEditableValue<Record<string, boolean>>("shown", {})
  const isMobile = useIsMobile()

  const film = props.film
  const images = film?.media?.gallery ?? []
  const gradientImages = images
    .map((image) => {
      if (!env.client) return null
      if (image.srcSet) {
        const url = new URL(image.srcSet.split(" ")[0])
        return url.toString()
      }
      return false
    })
    .filter(Boolean) as string[]

  const meta: ReactNode[] = META_TYPES.map((item) => {
    let show = item.showDefault
    if (itemConfig[item.label] !== undefined) {
      show = itemConfig[item.label]
    }
    if (show) {
      return item.get(film)
    }
  })
    .filter(Boolean)
    .map((item, i) => <div key={i}>{item}</div>)

  const onCarouselChange = (index: number) => {
    gradientRef.current?.useImg(index)
  }

  const sessions = (film?.sessions || []).filter(
    (s) => !!s?.info?.dateTime && !dayjs.tz().isAfter(dayjs.tz(s?.info?.dateTime)),
  )

  const program = props?.film?.program
  const isArchived = props.film?.program?.programInfo?.status === "Archived"

  return (
    <>
      {env.admin && (
        <InspectorControls>
          <div className="px-4 flex flex-col gap-1">
            {META_TYPES.map((credit) => {
              return (
                <Checkbox
                  key={credit.label}
                  label={credit.label}
                  checked={itemConfig[credit.label] ?? credit.showDefault}
                  onCheckedChange={(checked) => {
                    if (checked === credit.showDefault) {
                      delete itemConfig[credit.label]
                    } else {
                      itemConfig[credit.label] = checked
                    }
                    setItemConfig({ ...itemConfig })
                  }}
                />
              )
            })}
          </div>
        </InspectorControls>
      )}
      <div className="py-4 relative mb-8">
        <Gradient
          ref={gradientRef}
          img={gradientImages}
          className="absolute left-0 top-0 h-full"
          aspect="1/1"
          showImage={false}
          cutEdges={false}
          fadeEdges
          align="left"
          alpha={0.75}
        />
        <div className="flex justify-between relative pt-4 pb-6">
          <div className="flex flex-col gap-2 pl-4">
            <Link
              href={isArchived ? program?.uri : "/browse"}
              preferBack
              className="type-label-xs flex items-center gap-1 justify-center w-fit"
            >
              <IconChevronLeft className="w-[1.2em] h-[1.2em]" />
              <span className="mt-[0.2em] hover-link">
                {isArchived
                  ? `Back to ${program?.programInfo?.programTypeLabel + " " + program?.programInfo?.festivalYear} Archive`
                  : "All Films"}
              </span>
            </Link>
            <h1 className="type-title-xxl mt-5 md:pr-[4em]">{props.film?.title}</h1>
            <div className="flex gap-x-4 flex-wrap type-label-m">{meta}</div>
          </div>
          <div className="cols-film-sidebar relative -mt-3 md:mt-8">
            <div className="flex md:flex-col gap-3 absolute right-0 top-0 z-10">
              {sessions.length > 0 && (
                <Button
                  size={isMobile ? "sm" : "lg"}
                  className="justify-start md:pr-11"
                  onClick={() => {
                    document.getElementById("book-tickets-panel")?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                      inline: "nearest",
                    })
                  }}
                >
                  Book Tickets
                </Button>
              )}
              {film?.eventInfo?.trailer && (
                <PlayTrailerButton videoId={film.eventInfo.trailer}>
                  <Button size={isMobile ? "sm" : "lg"} variant="secondary" className="justify-start md:pr-11">
                    Watch Trailer
                  </Button>
                </PlayTrailerButton>
              )}
            </div>
          </div>
        </div>
        {images.length > 0 ? (
          // Multiple images, gallery mode
          <>
            {/* <div>
              <GallerySlider className="pt-0">
                {film?.media?.gallery.map((image, i) => (
                  <ResponsiveImage key={i} {...image} className="h-full relative" />
                ))}
              </GallerySlider>
            </div> */}
            <div className="relative px-4">
              <ParallaxCarousel className="h-homeCarouselH" images={images} onChange={onCarouselChange} />
            </div>
          </>
        ) : env.admin ? (
          <AdminMessage>No images have been added to this film, so the gallery carousel won't be shown.</AdminMessage>
        ) : null}
      </div>
    </>
  )
})

type MetaItem = {
  label: string
  showDefault?: boolean
  get: (props: BlockProps["film/film-header"]["film"]) => ReactNode
}

const META_TYPES: MetaItem[] = [
  {
    label: "Director",
    showDefault: true,
    get: (film) => (film?.credits?.director?.length ? `Directed by ${film?.credits?.director.join(", ")}` : null),
  },
  {
    label: "Country",
    showDefault: true,
    get: (film) => {
      const countries = film?.filmInfo?.country
      if (countries?.length) {
        const value = countries.join(", ")
        if (value.length < 100) {
          return value
        }
      }
    },
  },
  { label: "Year", showDefault: true, get: (film) => film?.filmInfo?.year },
  {
    label: "Runtime",
    showDefault: true,
    get: (film) => (film?.eventInfo?.runtime ? film?.eventInfo?.runtime + " mins" : null),
  },
]
